/**
 * AgentHighlightManager
 *
 * Manages visual feedback for Platform Assistant actions.
 * Handles element highlighting with a spotlight overlay effect — the entire screen
 * dims and only the target element stays bright, similar to onboarding tour libraries.
 */

/**
 * Configuration for highlight behavior
 */
export interface HighlightConfig {
  /** Duration of highlight before action (ms) */
  preActionDelay: number;
  /** Duration of highlight after action (ms) */
  postActionDelay: number;
  /** CSS class to apply for highlighting */
  highlightClass: string;
  /** Whether to scroll element into view */
  scrollIntoView: boolean;
  /** Scroll behavior */
  scrollBehavior: ScrollBehavior;
}

const DEFAULT_CONFIG: HighlightConfig = {
  preActionDelay: 300,
  postActionDelay: 300,
  highlightClass: 'agent-highlight',
  scrollIntoView: true,
  scrollBehavior: 'smooth',
};

/** Padding around the spotlight cutout (px) */
const SPOTLIGHT_PADDING = 8;
/** Border radius for the spotlight cutout (px) */
const SPOTLIGHT_BORDER_RADIUS = 8;
/** Overlay element ID */
const SPOTLIGHT_OVERLAY_ID = 'agent-spotlight-overlay';

/**
 * Manager for element highlighting and visual feedback.
 *
 * Uses a "spotlight" approach: a semi-transparent dark overlay covers the entire
 * screen, with a cutout around the highlighted element so it remains bright.
 * The cutout tracks the element position during scroll/resize.
 */
export class AgentHighlightManager {
  private config: HighlightConfig;
  private highlightedElements: Set<string> = new Set();
  private timeoutIds: Map<string, NodeJS.Timeout> = new Map();

  /** Active spotlight state for cleanup */
  private spotlightCleanup: (() => void) | null = null;

  constructor(config: Partial<HighlightConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.injectStyles();
  }

  /**
   * Inject highlight CSS styles into the document
   */
  private injectStyles(): void {
    if (typeof document === 'undefined') return;

    const styleId = 'agent-highlight-styles';

    // Remove any existing style element (handles HMR and code updates)
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* Spotlight overlay — covers the full viewport, darkens everything */
      #${SPOTLIGHT_OVERLAY_ID} {
        position: fixed;
        inset: 0;
        z-index: 9999;
        background: rgba(0, 0, 0, 0.55);
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      #${SPOTLIGHT_OVERLAY_ID}.agent-spotlight-visible {
        opacity: 1;
      }

      /* Highlighted element sits above the overlay */
      .agent-highlight {
        position: relative !important;
        z-index: 10000 !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Highlight an element by ID with spotlight overlay effect.
   *
   * @param elementId - The element ID (must have data-agent-element-id attribute)
   * @param durationMs - Optional duration override (auto-clears after this time)
   */
  highlight(elementId: string, durationMs?: number): void {
    const element = this.findElement(elementId);
    if (!element) {
      console.warn(`[AgentHighlightManager] Element not found: ${elementId}`);
      return;
    }

    // Clear any existing highlight/spotlight
    this.clearTimeout(elementId);
    this.removeSpotlight();

    // Scroll into view first so the element is visible before we spotlight it
    if (this.config.scrollIntoView) {
      element.scrollIntoView({
        behavior: this.config.scrollBehavior,
        block: 'center',
        inline: 'nearest',
      });
    }

    // Apply highlight class (elevates z-index above overlay)
    element.classList.add(this.config.highlightClass);
    this.highlightedElements.add(elementId);

    // Show spotlight overlay with cutout around the element
    this.showSpotlight(element);

    // Auto-clear if duration specified
    if (durationMs !== undefined && durationMs > 0) {
      const timeoutId = setTimeout(() => {
        this.clearHighlight(elementId);
      }, durationMs);
      this.timeoutIds.set(elementId, timeoutId);
    }
  }

  /**
   * Create and show the dark overlay with a rectangular cutout around the target element.
   * Uses an SVG mask so the cutout can have rounded corners.
   * Tracks element position on scroll/resize for the duration of the highlight.
   */
  private showSpotlight(element: HTMLElement): void {
    if (typeof document === 'undefined') return;

    // Create the overlay element
    const overlay = document.createElement('div');
    overlay.id = SPOTLIGHT_OVERLAY_ID;
    document.body.appendChild(overlay);

    // Build and apply the SVG mask based on the element's current position
    const updateMask = () => {
      const rect = element.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const x = rect.left - SPOTLIGHT_PADDING;
      const y = rect.top - SPOTLIGHT_PADDING;
      const w = rect.width + SPOTLIGHT_PADDING * 2;
      const h = rect.height + SPOTLIGHT_PADDING * 2;
      const r = SPOTLIGHT_BORDER_RADIUS;

      // SVG mask: white = visible (dark overlay), black rect = transparent (cutout)
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${vw}" height="${vh}">
          <defs>
            <mask id="spotlight-mask">
              <rect width="100%" height="100%" fill="white"/>
              <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" ry="${r}" fill="black"/>
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#spotlight-mask)"/>
        </svg>`;

      overlay.style.background = `url("data:image/svg+xml,${encodeURIComponent(svg.trim())}")`;
      overlay.style.backgroundSize = `${vw}px ${vh}px`;
    };

    updateMask();

    // Fade in
    requestAnimationFrame(() => {
      overlay.classList.add('agent-spotlight-visible');
    });

    // Track position changes (scroll anywhere on the page, or window resize)
    const onScroll = () => requestAnimationFrame(updateMask);
    const onResize = () => requestAnimationFrame(updateMask);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    // Store cleanup so we can tear down later
    this.spotlightCleanup = () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);

      // Fade out, then remove
      overlay.classList.remove('agent-spotlight-visible');
      setTimeout(() => overlay.remove(), 300);
    };
  }

  /**
   * Remove the spotlight overlay with a fade-out transition
   */
  private removeSpotlight(): void {
    if (this.spotlightCleanup) {
      this.spotlightCleanup();
      this.spotlightCleanup = null;
    }

    // Fallback: remove any orphaned overlay element
    const orphan = document.getElementById(SPOTLIGHT_OVERLAY_ID);
    if (orphan) orphan.remove();
  }

  /**
   * Clear highlight from an element
   */
  clearHighlight(elementId: string): void {
    const element = this.findElement(elementId);
    if (element) {
      element.classList.remove(this.config.highlightClass);
    }
    this.highlightedElements.delete(elementId);
    this.clearTimeout(elementId);
    this.removeSpotlight();
  }

  /**
   * Clear all highlights
   */
  clearAllHighlights(): void {
    this.highlightedElements.forEach((elementId) => {
      const element = this.findElement(elementId);
      if (element) {
        element.classList.remove(this.config.highlightClass);
      }
    });
    this.highlightedElements.clear();
    this.timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    this.timeoutIds.clear();
    this.removeSpotlight();
  }

  /**
   * Check if an element is currently highlighted
   */
  isHighlighted(elementId: string): boolean {
    return this.highlightedElements.has(elementId);
  }

  /**
   * Get all currently highlighted element IDs
   */
  getHighlightedElements(): string[] {
    return Array.from(this.highlightedElements);
  }

  /**
   * Execute the full highlight sequence for an action.
   *
   * 1. Spotlight the element (dark overlay + bright element)
   * 2. Wait pre-action delay
   * 3. Execute action callback
   * 4. Wait post-action delay
   * 5. Remove spotlight
   */
  async executeWithHighlight(
    elementId: string,
    actionCallback: () => void | Promise<void>
  ): Promise<void> {
    this.highlight(elementId);
    await this.delay(this.config.preActionDelay);
    await actionCallback();
    await this.delay(this.config.postActionDelay);
    this.clearHighlight(elementId);
  }

  /**
   * Find an element by its agent element ID
   */
  private findElement(elementId: string): HTMLElement | null {
    let element = document.querySelector(
      `[data-agent-element-id="${elementId}"]`
    ) as HTMLElement | null;

    if (!element) {
      element = document.getElementById(elementId);
    }

    return element;
  }

  /**
   * Clear a timeout for an element
   */
  private clearTimeout(elementId: string): void {
    const timeoutId = this.timeoutIds.get(elementId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.timeoutIds.delete(elementId);
    }
  }

  /**
   * Utility: delay for a given number of milliseconds
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<HighlightConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.clearAllHighlights();
    this.removeSpotlight();
  }
}
